import { showNotification } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/common/axios';
import { initiateIntegrationOauth } from '../utils/initiateIntegrationOauth';

interface IntegrationOauthStatusResponse {
	connected: boolean;
}

/**
 * Hook to check OAuth connection status for an integration
 */
export function useIntegrationOAuthStatus(
	integrationId: string | undefined,
	integrationType: string | undefined,
	enabled = true
) {
	const queryKey = [
		'integration-oauth-status',
		integrationId,
		integrationType,
	] as const;

	return useQuery<IntegrationOauthStatusResponse>({
		queryKey,
		queryFn: async () => {
			const response = await apiClient.get(
				`/integration_v2/integrations/${integrationId}/${integrationType}/oauth/status/`
			);
			return response.data as IntegrationOauthStatusResponse;
		},
		enabled: enabled && !!integrationId && !!integrationType,
	});
}

/**
 * Hook to initiate OAuth connection for an integration
 */
export function useIntegrationOAuthConnect(
	integrationId: string | undefined,
	integrationType: string | undefined,
	options?: {
		openInNewTab?: boolean;
		onSuccess?: () => void;
	}
) {
	const { openInNewTab = true, onSuccess } = options ?? {};

	return useMutation({
		mutationFn: async () => {
			if (!integrationId || !integrationType) {
				throw new Error('Integration ID and type are required');
			}
			await initiateIntegrationOauth(integrationId, integrationType, {
				openInNewTab,
			});
		},
		onSuccess: () => {
			if (openInNewTab) {
				showNotification({
					title: 'Authentication in progress',
					message:
						'Please complete the authentication in the new tab. Once complete, try running the query again.',
					color: 'blue',
					autoClose: 10000,
				});
			}
			onSuccess?.();
		},
		onError: (error: Error) => {
			showNotification({
				title: 'Authentication failed',
				message:
					error.message ||
					'Failed to initiate OAuth authentication. Please try again.',
				color: 'red',
			});
		},
	});
}

/**
 * Hook to disconnect OAuth for an integration
 */
export function useIntegrationOAuthDisconnect(
	integrationId: string | undefined,
	integrationType: string | undefined
) {
	const queryClient = useQueryClient();
	const queryKey = [
		'integration-oauth-status',
		integrationId,
		integrationType,
	] as const;

	return useMutation({
		mutationFn: async () => {
			if (!integrationId || !integrationType) {
				throw new Error('Integration ID and type are required');
			}
			await apiClient.delete(
				`/integration_v2/integrations/${integrationId}/${integrationType}/oauth/disconnect/`
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
}
